import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Maximize2, Minimize2, ScreenShare, ScreenShareOff, SwitchCamera, SignalHigh, SignalMedium, SignalLow, Pause, Lock, Volume1, Volume2, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { useCall } from '../../contexts/CallContext';

const formatDuration = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Draining countdown ring around the caller avatar (matches the group-call modal)
const CountdownAvatar = ({ name, expiresAt }) => {
  const [frac, setFrac] = useState(1);
  useEffect(() => {
    if (!expiresAt) return undefined;
    const total = Math.max(1000, expiresAt - Date.now());
    const t = setInterval(() => setFrac(Math.max(0, (expiresAt - Date.now()) / total)), 250);
    return () => clearInterval(t);
  }, [expiresAt]);
  const R = 46;
  const C = 2 * Math.PI * R;
  return (
    <div className="relative w-24 h-24 flex items-center justify-center">
      {expiresAt && (
        <svg className="absolute inset-0 w-24 h-24 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
          <circle
            cx="50" cy="50" r={R} fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - frac)}
            style={{ transition: 'stroke-dashoffset 0.3s linear' }}
            data-testid="incoming-call-countdown"
          />
        </svg>
      )}
      <div className="w-20 h-20 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-2xl font-semibold animate-pulse">
        {(name || '?').charAt(0).toUpperCase()}
      </div>
    </div>
  );
};

export const CallOverlay = () => {
  const {
    call, muted, cameraOn, screenSharing, remoteHasVideo, callSeconds, callQuality, peerPaused, encrypted,
    volumeBoost, toggleVolumeBoost, canBoostVolume,
    acceptCall, declineCall, endCall, toggleMute, toggleCamera, toggleScreenShare, flipCamera, cameraFacing, canFlipCamera,
    localStreamRef, remoteStreamRef,
    callChatToast, callChatUnread, clearCallChatUnread,
  } = useCall();
  const navigate = useNavigate();
  const isTouchDevice = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

  const EncryptionBadge = ({ dark, withText }) => {
    if (!encrypted || call?.status !== 'active') return null;
    return (
      <span
        title="Encrypted call"
        aria-label="Encrypted call"
        className={`inline-flex items-center gap-1 ${dark ? 'text-green-400' : 'text-green-600'}`}
        data-testid="call-encryption-badge"
      >
        <Lock className="w-3 h-3" />
        {withText && <span className="text-[10px] font-medium">Encrypted</span>}
      </span>
    );
  };

  const peerPausedText = peerPaused === 'away'
    ? `${call?.peer_name || 'They'} may be on another call or app`
    : 'Connection interrupted…';

  const QualityBadge = ({ dark }) => {
    if (!callQuality || call?.status !== 'active') return null;
    const colors = dark
      ? { good: 'text-green-400', fair: 'text-amber-400', poor: 'text-red-400' }
      : { good: 'text-green-600', fair: 'text-amber-500', poor: 'text-red-500' };
    const Icon = callQuality === 'poor' ? SignalLow : callQuality === 'fair' ? SignalMedium : SignalHigh;
    const label = callQuality === 'poor' ? 'Poor connection' : callQuality === 'fair' ? 'Fair connection' : 'Good connection';
    return (
      <span title={label} aria-label={label} className="inline-flex" data-testid="call-quality-badge" data-quality={callQuality}>
        <Icon className={`w-3.5 h-3.5 ${colors[callQuality]}`} />
      </span>
    );
  };

  const remoteVideoEl = useRef(null);
  const localVideoEl = useRef(null);
  const panelRef = useRef(null);
  const dragState = useRef(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [remoteAspect, setRemoteAspect] = useState(16 / 9);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const canScreenShare = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  const showVideoPanel = !!call && (call.status === 'active' || call.status === 'connecting') && (cameraOn || screenSharing || remoteHasVideo);

  // Reset panel position + aspect for each new call
  useEffect(() => {
    setDragPos({ x: 0, y: 0 });
    setRemoteAspect(16 / 9);
    setIsFullscreen(false);
  }, [call?.call_id]);

  // Match the panel to the remote camera's real aspect ratio (portrait phone vs landscape webcam)
  const handleRemoteDimensions = (e) => {
    const { videoWidth: w, videoHeight: h } = e.target;
    if (w && h) setRemoteAspect(w / h);
  };

  // Drag to move (pointer events cover mouse + touch)
  const onDragStart = (e) => {
    if (e.target.closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: dragPos.x,
      baseY: dragPos.y,
      rect: el.getBoundingClientRect(),
    };
    try { el.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const onDragMove = (e) => {
    const d = dragState.current;
    if (!d) return;
    const left0 = d.rect.left - d.baseX;
    const top0 = d.rect.top - d.baseY;
    let x = d.baseX + (e.clientX - d.startX);
    let y = d.baseY + (e.clientY - d.startY);
    x = Math.min(Math.max(x, 8 - left0), window.innerWidth - d.rect.width - 8 - left0);
    y = Math.min(Math.max(y, 8 - top0), window.innerHeight - d.rect.height - 8 - top0);
    setDragPos({ x, y });
  };
  const onDragEnd = () => { dragState.current = null; };
  const dragProps = {
    onPointerDown: onDragStart,
    onPointerMove: onDragMove,
    onPointerUp: onDragEnd,
    onPointerCancel: onDragEnd,
  };
  const dragStyle = { transform: `translate(${dragPos.x}px, ${dragPos.y}px)`, touchAction: 'none' };

  useEffect(() => {
    if (remoteVideoEl.current && remoteStreamRef.current && remoteVideoEl.current.srcObject !== remoteStreamRef.current) {
      remoteVideoEl.current.srcObject = remoteStreamRef.current;
    }
    if (localVideoEl.current && localStreamRef.current && localVideoEl.current.srcObject !== localStreamRef.current) {
      localVideoEl.current.srcObject = localStreamRef.current;
    }
  });

  if (!call) return null;

  const openChat = () => {
    clearCallChatUnread();
    setIsFullscreen(false);
    navigate(`/chat?conversation=${call.conversation_id}`);
  };

  // Incoming ring — full-screen modal
  if (call.role === 'callee' && call.status === 'ringing') {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="incoming-call-modal">
        <div className="bg-card border rounded-2xl shadow-2xl p-8 w-[320px] flex flex-col items-center gap-5">
          <CountdownAvatar name={call.peer_name} expiresAt={call.incoming_expires_at} />
          <div className="text-center">
            <p className="font-semibold text-lg" data-testid="incoming-call-name">{call.peer_name}</p>
            <p className="text-sm text-muted-foreground">Incoming {call.media} call…</p>
          </div>
          <div className="flex items-center gap-6">
            <Button
              onClick={declineCall}
              className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700 text-white"
              aria-label="Decline call"
              data-testid="incoming-call-decline"
            >
              <PhoneOff className="w-6 h-6" />
            </Button>
            <Button
              onClick={acceptCall}
              className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-700 text-white"
              aria-label="Accept call"
              data-testid="incoming-call-accept"
            >
              {call.media === 'video' ? <Video className="w-6 h-6" /> : <Phone className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const statusLabel =
    call.status === 'ringing' ? 'Calling…'
      : call.status === 'connecting' ? 'Connecting…'
        : formatDuration(callSeconds);

  // Video panel — remote full-bleed + local PiP + controls (floating or fullscreen)
  if (showVideoPanel) {
    const isPortrait = remoteAspect < 1;
    const btnCls = isFullscreen
      ? 'h-12 w-12 rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur'
      : 'h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur';
    const iconCls = isFullscreen ? 'w-5 h-5' : 'w-4 h-4';
    return (
      <div
        ref={panelRef}
        {...(isFullscreen ? {} : dragProps)}
        className={isFullscreen
          ? 'fixed inset-0 z-[10000] bg-black select-none'
          : `fixed bottom-4 right-4 z-[10000] ${isPortrait ? 'w-[220px] sm:w-[280px]' : 'w-[300px] sm:w-[380px]'} rounded-xl overflow-hidden border border-white/20 bg-black shadow-2xl select-none cursor-grab active:cursor-grabbing`}
        style={isFullscreen ? undefined : dragStyle}
        data-testid="call-video-panel"
      >
        <video
          ref={remoteVideoEl}
          autoPlay
          playsInline
          muted
          onLoadedMetadata={handleRemoteDimensions}
          onResize={handleRemoteDimensions}
          className={`${isFullscreen ? 'w-full h-full object-contain bg-black' : 'w-full object-cover bg-black'} ${!remoteHasVideo ? 'opacity-0' : ''}`}
          style={isFullscreen ? undefined : { aspectRatio: remoteAspect }}
          data-testid="call-remote-video"
        />
        {!remoteHasVideo && !peerPaused && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className={`${isFullscreen ? 'w-24 h-24 text-3xl' : 'w-16 h-16 text-xl'} rounded-full bg-[#2563eb] text-white flex items-center justify-center font-semibold`}>
              {(call.peer_name || '?').charAt(0).toUpperCase()}
            </div>
          </div>
        )}
        {peerPaused && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 pointer-events-none px-4" data-testid="call-peer-paused-overlay" data-reason={peerPaused}>
            <div className={`${isFullscreen ? 'w-16 h-16' : 'w-12 h-12'} rounded-full bg-amber-500/20 border border-amber-400/60 flex items-center justify-center animate-pulse`}>
              <Pause className={`${isFullscreen ? 'w-7 h-7' : 'w-5 h-5'} text-amber-400`} />
            </div>
            <p className={`${isFullscreen ? 'text-base' : 'text-sm'} text-white font-medium text-center`}>{peerPausedText}</p>
            <p className="text-xs text-white/70 text-center">Call paused — hold on, they should be right back</p>
          </div>
        )}
        {(cameraOn || screenSharing) && (
          <video
            ref={localVideoEl}
            autoPlay
            playsInline
            muted
            className={isFullscreen
              ? 'absolute top-4 right-4 w-32 sm:w-44 rounded-lg border border-white/40 object-cover shadow-lg'
              : 'absolute top-2 right-2 w-20 sm:w-24 rounded-md border border-white/40 object-cover'}
            style={(screenSharing || cameraFacing !== 'user') ? undefined : { transform: 'scaleX(-1)' }}
            data-testid="call-local-video"
          />
        )}
        <div className={`absolute ${isFullscreen ? 'top-4 left-4 px-3 py-2' : 'top-2 left-2 px-2 py-1'} rounded bg-black/50 text-white`}>
          <p className={`${isFullscreen ? 'text-sm' : 'text-xs'} font-semibold leading-tight`} data-testid="active-call-name">{call.peer_name}</p>
          <span className="flex items-center gap-1.5">
            <p className={`${isFullscreen ? 'text-xs' : 'text-[11px]'} opacity-80 leading-tight`} data-testid="active-call-status">{statusLabel}</p>
            <QualityBadge dark />
            <EncryptionBadge dark withText={isFullscreen} />
          </span>
        </div>
        {callChatToast && (
          <button
            onClick={openChat}
            className={`absolute ${isFullscreen ? 'top-16' : 'top-10'} left-1/2 -translate-x-1/2 z-20 max-w-[85%] flex items-center gap-2 rounded-full bg-neutral-800/95 border border-white/10 px-3 py-1.5 shadow-xl`}
            data-testid="call-chat-toast"
          >
            <MessageSquare className="w-3.5 h-3.5 text-blue-400 shrink-0" />
            <span className="text-xs text-white truncate">
              <span className="font-semibold">{callChatToast.sender}:</span> {callChatToast.text}
            </span>
          </button>
        )}
        <div className={`absolute ${isFullscreen ? 'bottom-8' : 'bottom-2'} inset-x-0 flex items-center justify-center gap-2`}>
          <Button
            size="icon"
            className={btnCls}
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-testid="active-call-mute"
          >
            {muted ? <MicOff className={`${iconCls} text-red-400`} /> : <Mic className={iconCls} />}
          </Button>
          {canBoostVolume && (
            <Button
              size="icon"
              className={btnCls}
              onClick={toggleVolumeBoost}
              aria-label={volumeBoost ? 'Switch to normal volume' : 'Switch to loud volume'}
              title={volumeBoost ? 'Loud speaker (tap for normal)' : 'Normal volume (tap for loud)'}
              data-testid="active-call-volume-panel"
              data-boost={volumeBoost ? 'on' : 'off'}
            >
              {volumeBoost ? <Volume2 className={`${iconCls} text-green-400`} /> : <Volume1 className={iconCls} />}
            </Button>
          )}
          <Button
            size="icon"
            className={btnCls}
            onClick={toggleCamera}
            aria-label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
            data-testid="active-call-camera"
          >
            {cameraOn ? <Video className={`${iconCls} text-green-400`} /> : <VideoOff className={iconCls} />}
          </Button>
          {cameraOn && !screenSharing && isTouchDevice && canFlipCamera && (
            <Button
              size="icon"
              className={btnCls}
              onClick={flipCamera}
              aria-label="Switch camera"
              data-testid="active-call-flip-camera"
            >
              <SwitchCamera className={iconCls} />
            </Button>
          )}
          {canScreenShare && (
            <Button
              size="icon"
              className={btnCls}
              onClick={toggleScreenShare}
              aria-label={screenSharing ? 'Stop sharing screen' : 'Share screen'}
              data-testid="active-call-screenshare"
            >
              {screenSharing ? <ScreenShareOff className={`${iconCls} text-green-400`} /> : <ScreenShare className={iconCls} />}
            </Button>
          )}
          <Button
            size="icon"
            className={`${btnCls} relative`}
            onClick={openChat}
            aria-label="Open the conversation"
            data-testid="active-call-chat"
          >
            <MessageSquare className={iconCls} />
            {callChatUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="call-chat-unread">
                {callChatUnread > 9 ? '9+' : callChatUnread}
              </span>
            )}
          </Button>
          <Button
            size="icon"
            className={btnCls}
            onClick={() => setIsFullscreen(f => !f)}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            data-testid="active-call-fullscreen"
          >
            {isFullscreen ? <Minimize2 className={iconCls} /> : <Maximize2 className={iconCls} />}
          </Button>
          <Button
            onClick={endCall}
            className={`${isFullscreen ? 'h-12 w-12' : 'h-10 w-10'} rounded-full bg-red-600 hover:bg-red-700 text-white`}
            aria-label="Hang up"
            data-testid="active-call-hangup"
          >
            <PhoneOff className={iconCls} />
          </Button>
        </div>
      </div>
    );
  }

  // Outgoing ring / connecting — centred on the page
  if (call.status === 'ringing' || call.status === 'connecting') {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="outgoing-call-modal">
        <div className="bg-card border rounded-2xl shadow-2xl p-8 w-[320px] flex flex-col items-center gap-5">
          <div className="w-20 h-20 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-2xl font-semibold animate-pulse">
            {(call.peer_name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="text-center">
            <p className="font-semibold text-lg" data-testid="active-call-name">{call.peer_name}</p>
            <p className="text-sm text-muted-foreground" data-testid="active-call-status">{statusLabel}</p>
          </div>
          <Button
            onClick={endCall}
            className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700 text-white"
            aria-label="Hang up"
            data-testid="active-call-hangup"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>
      </div>
    );
  }

  // Compact bar — audio-only active call
  return (
    <>
      {callChatToast && (
        <button
          onClick={openChat}
          className="fixed bottom-20 right-4 z-[10000] max-w-[280px] flex items-center gap-2 rounded-full border bg-card px-3 py-2 shadow-2xl"
          data-testid="call-chat-toast"
        >
          <MessageSquare className="w-4 h-4 text-blue-500 shrink-0" />
          <span className="text-xs truncate">
            <span className="font-semibold">{callChatToast.sender}:</span> {callChatToast.text}
          </span>
        </button>
      )}
      <div
      ref={panelRef}
      {...dragProps}
      className="fixed bottom-4 right-4 z-[10000] flex items-center gap-3 rounded-full border bg-card shadow-2xl pl-4 pr-2 py-2 select-none cursor-grab active:cursor-grabbing"
      style={dragStyle}
      data-testid="active-call-bar"
    >
      <span className={`w-2.5 h-2.5 rounded-full ${peerPaused ? 'bg-amber-500 animate-pulse' : call.status === 'active' ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight truncate max-w-[140px]" data-testid="active-call-name">{call.peer_name}</p>
        <span className="flex items-center gap-1.5">
          <p className={`text-xs leading-tight ${peerPaused ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`} data-testid="active-call-status">
            {peerPaused && call.status === 'active' ? peerPausedText : statusLabel}
          </p>
          {!peerPaused && <QualityBadge />}
          {!peerPaused && <EncryptionBadge />}
        </span>
      </div>
      {call.status === 'active' && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-testid="active-call-mute"
          >
            {muted ? <MicOff className="w-4 h-4 text-red-500" /> : <Mic className="w-4 h-4" />}
          </Button>
          {canBoostVolume && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={toggleVolumeBoost}
              aria-label={volumeBoost ? 'Switch to normal volume' : 'Switch to loud volume'}
              title={volumeBoost ? 'Loud speaker (tap for normal)' : 'Normal volume (tap for loud)'}
              data-testid="active-call-volume"
              data-boost={volumeBoost ? 'on' : 'off'}
            >
              {volumeBoost ? <Volume2 className="w-4 h-4 text-green-600" /> : <Volume1 className="w-4 h-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full"
            onClick={toggleCamera}
            aria-label="Turn camera on"
            data-testid="active-call-camera"
          >
            <VideoOff className="w-4 h-4" />
          </Button>
          {canScreenShare && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={toggleScreenShare}
              aria-label="Share screen"
              data-testid="active-call-screenshare"
            >
              <ScreenShare className="w-4 h-4" />
            </Button>
          )}
        </>
      )}
      {call.status === 'active' && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full relative"
          onClick={openChat}
          aria-label="Open the conversation"
          data-testid="active-call-chat"
        >
          <MessageSquare className="w-4 h-4" />
          {callChatUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="call-chat-unread">
              {callChatUnread > 9 ? '9+' : callChatUnread}
            </span>
          )}
        </Button>
      )}
      <Button
        onClick={endCall}
        className="h-9 w-9 rounded-full bg-red-600 hover:bg-red-700 text-white"
        aria-label="Hang up"
        data-testid="active-call-hangup"
      >
        <PhoneOff className="w-4 h-4" />
      </Button>
    </div>
    </>
  );
};
